import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {AbortController | null} */
  #historyCartController = null;

  /** Bumped on every cart update so a slower history read cannot overwrite a newer cart. @type {number} */
  #cartRenderRevision = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();

    // A page rebuilt for Back/Forward (not frozen) can finish loading after its own `pageshow` has fired.
    if (CartIcon.#getNavigationType() === 'back_forward') {
      this.#reconcileHistoryCart();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    this.#historyCartController?.abort();
    this.#historyCartController = null;
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = async (event) => {
    if (!event.persisted) return;

    this.ensureCartBubbleIsCorrect();
    await this.#reconcileHistoryCart();
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (event.detail.data?.didError) return;

    this.#cartRenderRevision += 1;
    this.#historyCartController?.abort();

    const itemCount = event.detail.data?.itemCount ?? 0;
    // These sources report the quantity just added, not the cart total.
    const comingFromProductForm = ['product-form-component', 'nosto-recommendations'].includes(
      event.detail.data?.source ?? ''
    );

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Reconciles markup restored from history (frozen or rebuilt from the HTTP cache) with the real cart.
   * The icon's stored count only lives 10 seconds, and the drawer is plain cached markup, so after a trip
   * through checkout both can show an empty cart until reload.
   */
  #reconcileHistoryCart = async () => {
    this.#historyCartController?.abort();
    const controller = new AbortController();
    const cartRenderRevision = this.#cartRenderRevision;
    this.#historyCartController = controller;

    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

      const cart = await response.json();
      const itemCount = cart?.item_count;
      if (!Number.isInteger(itemCount) || itemCount < 0) throw new Error('Cart response has an invalid item count');

      // A cart update landed meanwhile and is newer than this read.
      if (controller.signal.aborted || this.#cartRenderRevision !== cartRenderRevision) return;

      await this.renderCartBubble(itemCount, false, false);
      await this.#refreshCartSections(controller.signal);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[cart-icon] History reconciliation failed:', error);
    } finally {
      if (this.#historyCartController === controller) this.#historyCartController = null;
    }
  };

  /**
   * Re-renders every cart (drawer, cart page) from the server, without announcing a cart change.
   * @param {AbortSignal} signal
   */
  async #refreshCartSections(signal) {
    // A rebuilt page can run this before the cart module upgrades its elements.
    await customElements.whenDefined('cart-items-component');
    if (signal.aborted) return;

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((element) => {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    });

    await Promise.all([...sectionIds].map((id) => sectionRenderer.renderSection(id, { cache: false })));
  }

  /**
   * Returns the current document's navigation type.
   * @returns {string | undefined}
   */
  static #getNavigationType() {
    const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    return entry?.entryType === 'navigation' ? /** @type {PerformanceNavigationTiming} */ (entry).type : undefined;
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
